/**
 * RaceSimulator
 *
 * Simulates fake participants joining and racing for demo purposes.
 * Creates realistic-looking race progress with varying skill levels.
 * Uses Edge Function to bypass RLS for simulated participants.
 */

import { supabase } from '@/lib/supabase';

// Fake student names for simulation
const FAKE_NAMES = [
  'Emma', 'Liam', 'Olivia', 'Noah', 'Ava', 'Ethan', 'Sophia', 'Mason',
  'Isabella', 'William', 'Mia', 'James', 'Charlotte', 'Benjamin', 'Amelia',
  'Lucas', 'Harper', 'Henry', 'Evelyn', 'Alexander', 'Abigail', 'Michael',
  'Emily', 'Daniel', 'Elizabeth', 'Jacob', 'Sofia', 'Logan', 'Avery', 'Jackson',
];

const APP_OPTIONS = ['addition', 'multiplication'];

interface SimulatedParticipant {
  odoo: string; // Participant row ID (UUID format)
  odot: string; // Username
  app_name: string;
  skill: number; // 0.5-1.0, higher = faster/more accurate
  intervalId?: ReturnType<typeof setTimeout>;
  cardsCompleted: number;
  correctCount: number;
  incorrectCount: number;
  isFinished: boolean;
}

export class RaceSimulator {
  private raceRoomId: string;
  private questionCount: number;
  private participants: SimulatedParticipant[] = [];
  private isRunning = false;

  constructor(raceRoomId: string, questionCount: number) {
    this.raceRoomId = raceRoomId;
    this.questionCount = questionCount;
  }

  /**
   * Call the race-simulator Edge Function
   */
  private async callEdgeFunction(body: Record<string, unknown>): Promise<{ success: boolean; error?: string; ids?: string[] }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        return { success: false, error: 'Not authenticated' };
      }

      console.log('Calling edge function with:', body.action);
      const response = await supabase.functions.invoke('race-simulator', {
        body,
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      console.log('Edge function response:', response);

      if (response.error) {
        console.error('Edge function error:', response.error);
        return { success: false, error: response.error.message };
      }

      if (response.data?.error) {
        console.error('Edge function data error:', response.data.error);
        return { success: false, error: response.data.error };
      }

      return { success: true, ids: response.data?.ids };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('Edge function exception:', err);
      return { success: false, error: message };
    }
  }

  /**
   * Generate a UUID for tracking simulated participants locally
   */
  private generateLocalId(): string {
    return crypto.randomUUID();
  }

  /**
   * Add simulated participants to the race
   */
  async addParticipants(count: number): Promise<{ success: boolean; error?: string }> {
    try {
      const shuffledNames = [...FAKE_NAMES].sort(() => Math.random() - 0.5);
      const namesToUse = shuffledNames.slice(0, Math.min(count, FAKE_NAMES.length));

      const participantsToCreate = namesToUse.map((name) => ({
        odot: name,
        app_name: APP_OPTIONS[Math.floor(Math.random() * APP_OPTIONS.length)],
        skill: 0.5 + Math.random() * 0.5, // Random skill 0.5-1.0
      }));

      // Insert participants via Edge Function (returns created IDs)
      const result = await this.callEdgeFunction({
        action: 'add-participants',
        raceRoomId: this.raceRoomId,
        participants: participantsToCreate.map((p) => ({
          odot: p.odot,
          app_name: p.app_name,
        })),
      });

      if (!result.success) {
        console.error('Failed to insert simulated participants:', result.error);
        return result;
      }

      // Create local tracking objects with returned IDs
      const createdIds = result.ids || [];
      const newParticipants: SimulatedParticipant[] = participantsToCreate.map((p, i) => ({
        odoo: createdIds[i] || this.generateLocalId(),
        odot: p.odot,
        app_name: p.app_name,
        skill: p.skill,
        cardsCompleted: 0,
        correctCount: 0,
        incorrectCount: 0,
        isFinished: false,
      }));

      this.participants.push(...newParticipants);
      console.log(`Added ${count} simulated participants`);
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      return { success: false, error: message };
    }
  }

  /**
   * Start simulating race progress for all participants
   * Each participant answers at their own pace based on skill level
   */
  startSimulation(): void {
    if (this.isRunning) return;
    this.isRunning = true;

    console.log(`Starting race simulation for ${this.participants.length} participants`);

    for (const participant of this.participants) {
      // Calculate answer interval based on skill (1.5-4 seconds per question)
      const baseInterval = 4000 - participant.skill * 2500; // 1.5s to 4s
      const randomVariation = () => baseInterval * (0.7 + Math.random() * 0.6);

      const simulateAnswer = async () => {
        if (participant.isFinished || !this.isRunning) return;

        // Determine if answer is correct based on skill
        const isCorrect = Math.random() < (0.6 + participant.skill * 0.35); // 60-95% accuracy

        participant.cardsCompleted++;
        if (isCorrect) {
          participant.correctCount++;
        } else {
          participant.incorrectCount++;
        }

        // Update live_stats via Edge Function
        await this.updateLiveStats(participant);

        // Check if finished
        if (participant.cardsCompleted >= this.questionCount) {
          participant.isFinished = true;
          await this.finishParticipant(participant);
          if (participant.intervalId) {
            clearTimeout(participant.intervalId);
          }
          console.log(`${participant.odot} finished: ${participant.correctCount}/${participant.cardsCompleted}`);
        } else {
          // Schedule next answer with variable timing
          if (participant.intervalId) {
            clearTimeout(participant.intervalId);
          }
          participant.intervalId = setTimeout(simulateAnswer, randomVariation());
        }
      };

      // Start first answer after a random delay (stagger start times)
      const initialDelay = Math.random() * 2000;
      participant.intervalId = setTimeout(simulateAnswer, initialDelay);
    }
  }

  /**
   * Update live_stats for a simulated participant
   */
  private async updateLiveStats(participant: SimulatedParticipant): Promise<void> {
    await this.callEdgeFunction({
      action: 'update-stats',
      raceRoomId: this.raceRoomId,
      participantId: participant.odoo,
      cardsCompleted: participant.cardsCompleted,
      correctCount: participant.correctCount,
      incorrectCount: participant.incorrectCount,
    });
  }

  /**
   * Mark a simulated participant as finished
   */
  private async finishParticipant(participant: SimulatedParticipant): Promise<void> {
    await this.callEdgeFunction({
      action: 'finish-participant',
      raceRoomId: this.raceRoomId,
      participantId: participant.odoo,
      totalCards: participant.cardsCompleted,
      correctCount: participant.correctCount,
      incorrectCount: participant.incorrectCount,
    });
  }

  /**
   * Stop the simulation and cleanup
   */
  stopSimulation(): void {
    this.isRunning = false;
    for (const participant of this.participants) {
      if (participant.intervalId) {
        clearTimeout(participant.intervalId);
      }
    }
    console.log('Race simulation stopped');
  }

  /**
   * Remove all simulated participants from the database
   */
  async cleanup(): Promise<void> {
    this.stopSimulation();

    const userIds = this.participants.map((p) => p.odoo);
    if (userIds.length === 0) return;

    await this.callEdgeFunction({
      action: 'cleanup',
      raceRoomId: this.raceRoomId,
      participantIds: userIds,
    });

    this.participants = [];
    console.log('Simulated participants cleaned up');
  }

  /**
   * Get the number of simulated participants
   */
  getParticipantCount(): number {
    return this.participants.length;
  }

  /**
   * Check if all participants have finished
   */
  allFinished(): boolean {
    return this.participants.length > 0 && this.participants.every((p) => p.isFinished);
  }
}
